/**
 * AIModelSelector Component
 * Dropdown selector for AI models with parameter configuration modal.
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  getModelsForStudy,
  runModel,
} from '../../services/aiModels';
import './AIModelSelector.css';

const AIModelSelector = ({
  studyId,
  patientId,
  imageId,
  onExecutionStarted,
  disabled = false,
}) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showParamsModal, setShowParamsModal] = useState(false);
  const [parameters, setParameters] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const dropdownRef = useRef(null);

  // Load models when studyId changes
  useEffect(() => {
    if (studyId) {
      loadModels();
    }
  }, [studyId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadModels = async () => {
    setLoading(true);
    try {
      const data = await getModelsForStudy(studyId);
      setModels(data);
    } catch (err) {
      console.error('Error loading AI models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setIsOpen(false);

    // Set default version if available
    if (model.default_version) {
      setSelectedVersion(model.default_version);
    } else if (model.versions && model.versions.length > 0) {
      setSelectedVersion(model.versions[0]);
    } else {
      setSelectedVersion(null);
    }

    // Initialize parameters with defaults
    const defaultParams = { ...model.default_parameters };
    setParameters(defaultParams);

    // Show params modal if model has configurable parameters or multiple versions
    const hasParams = model.parameters_schema && Object.keys(model.parameters_schema).length > 0;
    const hasMultipleVersions = model.versions && model.versions.length > 1;
    const isTextModel = model.model_type === 'qa' || model.input_types === 'text';

    if (hasParams || hasMultipleVersions || isTextModel) {
      setShowParamsModal(true);
    } else {
      // Run directly if no parameters to configure and single version
      executeModel(model, defaultParams, model.default_version?.id || null);
    }
  };

  const executeModel = async (model, params, versionId = null) => {
    if (!patientId) {
      toast.error('No patient selected');
      return;
    }

    // Log which patient we're running the model on for debugging
    console.log(`[AIModelSelector] Running ${model.name} on patient: ${patientId}, imageId: ${imageId || 'not provided'}`);

    setIsRunning(true);
    try {
      const requestData = {
        patient_id: patientId,
        study_id: studyId,
        parameters: params,
      };

      // Include image_id only if available
      // Backend will look up image by patient_id if not provided
      if (imageId) {
        requestData.image_id = imageId;
        console.log(`[AIModelSelector] Using provided imageId: ${imageId}`);
      } else {
        console.log(`[AIModelSelector] No imageId provided, backend will lookup by patient_id: ${patientId}`);
      }

      // Include version_id if specified
      if (versionId) {
        requestData.version_id = versionId;
      }

      const execution = await runModel(model.id, requestData);

      const versionInfo = selectedVersion ? ` (${selectedVersion.version_tag})` : '';
      toast.success(`Started ${model.name}${versionInfo} processing`);
      setShowParamsModal(false);
      setSelectedModel(null);
      setSelectedVersion(null);

      if (onExecutionStarted) {
        onExecutionStarted(execution);
      }
    } catch (err) {
      console.error('Error starting model execution:', err);
      toast.error(err.response?.data?.error || 'Failed to start model');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunWithParams = () => {
    if (selectedModel) {
      // Validate query for QA/text models
      if ((selectedModel.model_type === 'qa' || selectedModel.input_types === 'text') && !parameters.query?.trim()) {
        toast.error('Please enter a question');
        return;
      }
      executeModel(selectedModel, parameters, selectedVersion?.id || null);
    }
  };

  const handleVersionChange = (version) => {
    setSelectedVersion(version);
  };

  const handleParamChange = (key, value) => {
    setParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleMultiSelectToggle = (key, option) => {
    setParameters((prev) => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.includes(option)
        ? currentValues.filter((v) => v !== option)
        : [...currentValues, option];
      return { ...prev, [key]: newValues };
    });
  };

  const getServerStatusIndicator = (model) => {
    if (!model.keep_loaded) return null;
    const statusConfig = {
      stopped: { color: '#6c757d', icon: 'fa-circle', label: 'Stopped' },
      starting: { color: '#ffc107', icon: 'fa-circle fa-fade', label: 'Starting...' },
      running: { color: '#28a745', icon: 'fa-circle', label: 'Running' },
      error: { color: '#dc3545', icon: 'fa-circle', label: 'Error' },
    };
    const status = statusConfig[model.server_status] || statusConfig.stopped;
    return (
      <span className="ai-server-status" title={`Server: ${status.label}`}>
        <i className={`fa ${status.icon}`} style={{ color: status.color, fontSize: '8px' }}></i>
      </span>
    );
  };

  const renderParameterInput = (key, config) => {
    const value = parameters[key];

    switch (config.type) {
      case 'boolean':
        return (
          <label className="ai-param-checkbox">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleParamChange(key, e.target.checked)}
            />
            <span>{config.label}</span>
          </label>
        );

      case 'select':
        return (
          <div className="ai-param-select-wrapper">
            <label>{config.label}</label>
            <select
              value={value || ''}
              onChange={(e) => handleParamChange(key, e.target.value)}
            >
              <option value="">Select...</option>
              {config.options?.map((opt) => {
                // Handle both string options and {value, label} object options
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <option key={optValue} value={optValue}>
                    {optLabel}
                  </option>
                );
              })}
            </select>
          </div>
        );

      case 'multiselect':
        return (
          <div className="ai-param-multiselect">
            <label>{config.label}</label>
            <div className="ai-param-options">
              {config.options?.map((opt) => {
                // Handle both string options and {value, label} object options
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <label key={optValue} className="ai-param-option">
                    <input
                      type="checkbox"
                      checked={(value || []).includes(optValue)}
                      onChange={() => handleMultiSelectToggle(key, optValue)}
                    />
                    <span>{optLabel}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'number':
        return (
          <div className="ai-param-number-wrapper">
            <label>{config.label}</label>
            <input
              type="number"
              value={value ?? config.default ?? ''}
              min={config.min}
              max={config.max}
              onChange={(e) => handleParamChange(key, parseFloat(e.target.value))}
            />
          </div>
        );

      case 'text':
        return (
          <div className="ai-param-text-wrapper">
            <label>{config.label}</label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleParamChange(key, e.target.value)}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="ai-param-textarea-wrapper">
            <label>{config.label}</label>
            <textarea
              value={value || ''}
              onChange={(e) => handleParamChange(key, e.target.value)}
              placeholder={config.placeholder || ''}
              rows={config.rows || 4}
              className="ai-param-textarea"
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (models.length === 0 && !loading) {
    return null; // Don't show if no models available
  }

  return (
    <div className="ai-model-selector" ref={dropdownRef}>
      {/* Main Dropdown Button */}
      <button
        className={`ai-model-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || loading || !patientId}
        title={!patientId ? 'Select a patient first' : `Run AI Model on ${patientId}`}
      >
        <i className="fa fa-brain"></i>
        <span>AI Models</span>
        {loading ? (
          <i className="fa fa-spinner fa-spin"></i>
        ) : (
          <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="ai-model-dropdown">
          <div className="ai-model-dropdown-header">
            <i className="fa fa-brain"></i>
            <span>Select AI Model</span>
          </div>
          <div className="ai-model-list">
            {models.map((model) => (
              <button
                key={model.id}
                className="ai-model-item"
                onClick={() => handleModelSelect(model)}
              >
                <i
                  className={`fa ${model.icon}`}
                  style={{ color: model.color }}
                ></i>
                <div className="ai-model-info">
                  <span className="ai-model-name">
                    {model.name}
                    {getServerStatusIndicator(model)}
                  </span>
                  <span className="ai-model-type">
                    {model.model_type_display}
                  </span>
                </div>
                <span className="ai-model-runtime">
                  ~{Math.round(model.estimated_runtime / 60)}m
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Parameters Modal */}
      {showParamsModal && selectedModel && (
        <div className="ai-params-modal-overlay" onClick={() => setShowParamsModal(false)}>
          <div className="ai-params-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-params-modal-header">
              <i
                className={`fa ${selectedModel.icon}`}
                style={{ color: selectedModel.color }}
              ></i>
              <span>{selectedModel.name}</span>
              <button
                className="ai-params-close-btn"
                onClick={() => setShowParamsModal(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            {selectedModel.description && (
              <div className="ai-params-description">
                {selectedModel.description}
              </div>
            )}

            {/* Version Selector */}
            {selectedModel.versions && selectedModel.versions.length > 1 && (
              <div className="ai-version-selector">
                <label className="ai-version-label">
                  <i className="fa fa-code-branch"></i>
                  Version
                </label>
                <div className="ai-version-options">
                  {selectedModel.versions.map((version) => (
                    <button
                      key={version.id}
                      className={`ai-version-btn ${selectedVersion?.id === version.id ? 'active' : ''}`}
                      onClick={() => handleVersionChange(version)}
                      title={version.release_notes || `Version ${version.version_tag}`}
                    >
                      <span className="ai-version-tag">{version.version_tag}</span>
                      {version.is_default && (
                        <span className="ai-version-default">default</span>
                      )}
                      {version.commit_sha && (
                        <span className="ai-version-sha">{version.commit_sha}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Single version info */}
            {selectedModel.versions && selectedModel.versions.length === 1 && (
              <div className="ai-version-info">
                <i className="fa fa-code-branch"></i>
                <span>Version: {selectedModel.versions[0].version_tag}</span>
              </div>
            )}

            <div className="ai-params-body">
              {Object.entries(selectedModel.parameters_schema || {}).map(
                ([key, config]) => (
                  <div key={key} className="ai-param-group">
                    {renderParameterInput(key, config)}
                    {config.description && (
                      <span className="ai-param-help">{config.description}</span>
                    )}
                  </div>
                )
              )}
            </div>

            <div className="ai-params-footer">
              <button
                className="ai-params-cancel-btn"
                onClick={() => setShowParamsModal(false)}
              >
                Cancel
              </button>
              <button
                className="ai-params-run-btn"
                onClick={handleRunWithParams}
                disabled={isRunning}
              >
                {isRunning ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <i className="fa fa-play"></i>
                    <span>Run Model</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIModelSelector;
